import React from 'react';
import { cn } from '../../utils';

/** Shared class for &lt;select&gt;, &lt;textarea&gt; (matches `Input` style) */
export const formFieldControlClassName = cn(
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
  'placeholder:text-muted-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

const spacingClass = {
  default: 'space-y-4',
  compact: 'space-y-3',
  relaxed: 'space-y-6',
} as const;

export interface CommonFormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  children: React.ReactNode;
  spacing?: keyof typeof spacingClass;
}

/** &lt;form&gt; wrapper — consistent vertical spacing */
export const CommonForm = React.forwardRef<HTMLFormElement, CommonFormProps>(
  ({ children, className, spacing = 'default', ...props }, ref) => (
    <form
      ref={ref}
      className={cn(spacingClass[spacing], className)}
      {...props}
    >
      {children}
    </form>
  ),
);
CommonForm.displayName = 'CommonForm';

export interface FormFieldProps {
  label?: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  description?: React.ReactNode;
  error?: string;
  className?: string;
  children: React.ReactNode;
}

/** Single field: label + short description + control + error */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  required,
  description,
  error,
  className,
  children,
}) => (
  <div className={cn('space-y-2', className)}>
    {(label != null || description != null) && (
      <div className="space-y-1">
        {label != null && (
          <label
            htmlFor={htmlFor}
            className="block text-sm font-medium leading-none text-foreground"
          >
            {label}
            {required ? <span className="ml-0.5 text-destructive">*</span> : null}
          </label>
        )}
        {description != null && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
    )}
    {children}
    {error ? <p className="text-sm text-destructive">{error}</p> : null}
  </div>
);

export interface FormGridProps {
  children: React.ReactNode;
  className?: string;
  /** Column count from `md` breakpoint (default 2) */
  columns?: 1 | 2 | 3;
  /** Vertical alignment in grid (e.g. `end` to align checkbox with select) */
  align?: 'start' | 'end' | 'center';
}

const formGridCols: Record<NonNullable<FormGridProps['columns']>, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
};

const formGridAlign: Record<NonNullable<FormGridProps['align']>, string> = {
  start: 'items-start',
  end: 'items-end',
  center: 'items-center',
};

/** Responsive grid for side-by-side fields */
export const FormGrid: React.FC<FormGridProps> = ({
  children,
  className,
  columns = 2,
  align = 'start',
}) => (
  <div
    className={cn(
      'grid grid-cols-1 gap-4',
      formGridCols[columns],
      formGridAlign[align],
      className,
    )}
  >
    {children}
  </div>
);

export interface FormSectionProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Top-right button / action (e.g. Configure) */
  action?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

/** Bordered block: title + description + (optional) action + content */
export const FormSection: React.FC<FormSectionProps> = ({
  title,
  description,
  action,
  className,
  children,
}) => (
  <div
    className={cn(
      'space-y-3 rounded-lg border border-border bg-muted/20 p-4',
      className,
    )}
  >
    {(title != null || description != null || action != null) && (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          {title != null && (
            <p className="text-sm font-medium text-foreground">{title}</p>
          )}
          {description != null && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
        {action != null ? <div className="shrink-0">{action}</div> : null}
      </div>
    )}
    {children}
  </div>
);

export interface FormActionsProps {
  children: React.ReactNode;
  className?: string;
}

/** Bottom form button row (Cancel / Submit) */
export const FormActions: React.FC<FormActionsProps> = ({ children, className }) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3',
      className,
    )}
  >
    {children}
  </div>
);

export const FormSelect = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, ...props }, ref) => (
  <select ref={ref} className={cn(formFieldControlClassName, className)} {...props} />
));
FormSelect.displayName = 'FormSelect';

export const FormTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      formFieldControlClassName,
      'min-h-[88px] resize-y py-2',
      className,
    )}
    {...props}
  />
));
FormTextarea.displayName = 'FormTextarea';

export interface FormCheckboxFieldProps {
  id: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  checked: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  className?: string;
}

/** Checkbox + label (same row), for Active / toggle */
export const FormCheckboxField: React.FC<FormCheckboxFieldProps> = ({
  id,
  label,
  description,
  checked,
  onChange,
  disabled,
  className,
}) => (
  <div className={cn('flex items-start gap-2', className)}>
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="mt-0.5 h-4 w-4 shrink-0 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
    />
    <div className="min-w-0 space-y-0.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground cursor-pointer">
        {label}
      </label>
      {description != null && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  </div>
);

/** Label for horizontal radio/checkbox group (shared style) */
export const formChoiceRowClassName = 'flex flex-wrap gap-4 text-sm';
export const formChoiceLabelClassName =
  'inline-flex cursor-pointer items-center gap-2 text-foreground';
